/**
 * How a print job is described in the queue drawer.
 *
 * Pure functions, kept out of the component so the rules that decide when a
 * job is downloadable and how far along it is can be tested directly — that
 * is where the drawer got it wrong before, offering a Download button on a
 * job that was still rendering.
 */

export interface QueueJob {
  id: string;
  title: string | null;
  mapType: string | null;
  status: string;
  createdAt: string;
  pdfUrl: string | null;
  previewUrl: string | null;
  pageCount: number | null;
  renderedPageCount: number | null;
  errorMessage: string | null;
  pageSize: string | null;
  orientation: string | null;
  effectiveScaleDenominator: number | null;
}

export type JobStatusFields = Pick<QueueJob, 'status'>;

export function isWorking(job: JobStatusFields): boolean {
  return job.status === 'pending' || job.status === 'processing';
}

/**
 * A PDF alone is not enough. Re-running a job (a Sidekiq retry, or the same
 * sheet queued again) leaves the previous document attached until the new one
 * replaces it, so downloading mid-render hands the user last time's map.
 */
export function isDownloadable(job: Pick<QueueJob, 'status' | 'pdfUrl'>): boolean {
  return job.status === 'completed' && Boolean(job.pdfUrl);
}

export function statusLabel(job: Pick<QueueJob, 'status' | 'pageCount' | 'renderedPageCount'>): string {
  switch (job.status) {
    case 'pending':
      return 'Queued';
    case 'processing': {
      const total = job.pageCount || 1;
      if (total <= 1) return 'Rendering';
      // The renderer reports pages as it finishes them, so the page being
      // worked on is one past the last completed one — capped so the label
      // never claims a page beyond the end of the document.
      return `Rendering ${Math.min((job.renderedPageCount || 0) + 1, total)} of ${total}`;
    }
    case 'completed':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return job.status;
  }
}

export function progressPercent(job: Pick<QueueJob, 'pageCount' | 'renderedPageCount'>): number {
  const total = job.pageCount || 1;
  const done = Math.min(Math.max(job.renderedPageCount || 0, 0), total);
  // A small floor so a job that has only just started still shows a bar
  // rather than an empty track.
  return Math.min(100, Math.max(3, Math.round((done / total) * 100)));
}

/** "A4 portrait · 1:25,000 · 28 pages" */
export function sheetSummary(job: Pick<QueueJob, 'pageSize' | 'orientation' | 'effectiveScaleDenominator' | 'pageCount'>): string {
  const parts: string[] = [];
  if (job.pageSize) parts.push(`${job.pageSize.toUpperCase()} ${job.orientation || ''}`.trim());
  if (job.effectiveScaleDenominator) parts.push(`1:${job.effectiveScaleDenominator.toLocaleString('en-US')}`);
  if ((job.pageCount || 1) > 1) parts.push(`${job.pageCount} pages`);
  return parts.join(' · ');
}

export function workingCount(jobs: JobStatusFields[]): number {
  return jobs.filter(isWorking).length;
}

/**
 * The line under the drawer title: "3 rendering · 12 recent".
 *
 * Derives the in-flight count from the same list it counts, so the two halves
 * can't disagree.
 */
export function queueHeaderSummary(jobs: JobStatusFields[]): string {
  const working = workingCount(jobs);
  if (working > 0) return `${working} rendering · ${jobs.length} recent`;
  return jobs.length ? `${jobs.length} recent` : 'No print maps yet';
}

export const ACTIVE_POLL_MS = 5000;
/** An open drawer keeps converging even with nothing in flight. */
export const OPEN_IDLE_POLL_MS = 15000;
export const IDLE_POLL_MS = 60000;

/**
 * How long to wait before the next poll.
 *
 * An open drawer never stops polling: if a refresh is lost — the dev server
 * restarting mid-render, a dropped connection — the card would otherwise sit
 * on its last known state indefinitely, which reads as "it finished but never
 * updated".
 */
export function pollDelay(jobs: JobStatusFields[], drawerOpen: boolean): number {
  if (workingCount(jobs) > 0) return ACTIVE_POLL_MS;
  return drawerOpen ? OPEN_IDLE_POLL_MS : IDLE_POLL_MS;
}

/** A closed, idle drawer has nothing to ask the server for. */
export function shouldRefresh(jobs: JobStatusFields[], drawerOpen: boolean): boolean {
  return drawerOpen || workingCount(jobs) > 0;
}

/** Newly created jobs go to the front, without duplicating anything already listed. */
export function mergeJobs(existing: QueueJob[], incoming: QueueJob[]): QueueJob[] {
  const incomingIds = new Set(incoming.map((job) => job.id));
  return [...incoming, ...existing.filter((job) => !incomingIds.has(job.id))];
}
